import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-primary-600)] text-white hover:bg-[var(--color-primary-700)] border-[var(--color-primary-600)]',
        defaultBorder:
          'bg-white text-[var(--color-primary-600)] hover:bg-[var(--color-primary-100)] border border-[var(--color-primary-600)]',
        secondary:
          'bg-[var(--color-secondary-100)] text-[var(--color-secondary-700)] hover:bg-blue-200 border-[var(--color-secondary-100)]',
        success: 'bg-[var(--color-success-500)] text-white hover:bg-green-600 border-[var(--color-success-500)]',
        warning: 'bg-[var(--color-warning-500)] text-white hover:bg-orange-600 border-[var(--color-warning-500)]',
        error: 'bg-[var(--color-error-700)] text-white hover:bg-red-500 border-[var(--color-error-500)]',
        destructive: 'bg-[var(--color-error-500)] text-white hover:bg-red-600 border-[var(--color-error-500)]',
        outline:
          'border-2 border-[var(--color-neutral-300)] bg-transparent text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]',
        ghost: 'bg-transparent text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]',
        link: 'bg-transparent underline-offset-4 hover:underline text-[var(--color-primary-600)]',
      },
      size: {
        sm: 'h-11 px-3 text-sm has-[>svg]:px-2.5',
        md: 'h-11 px-4 py-2 has-[>svg]:px-3',
        lg: 'h-12 px-6 text-lg has-[>svg]:px-4',
        icon: 'h-11 w-11',
      },
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
